/**
 * ChatMessages - Simplified UI Renderer
 * 
 * Renders UI descriptors using components from the catalog registry.
 * Simplified to work with the new descriptor format:
 * { id, component, type, data }
 */

import PropTypes from 'prop-types';
import { memo } from 'react';
import ChatMessageContainer from './ChatMessageContainer';

// ============================================================================
// Main ChatMessages Component
// ============================================================================

/**
 * Renders a list of UI descriptors
 */
const ChatMessages = memo(({
    descriptors = [],
    onAction,
    emptyState = null,
    className = '',
    containerRef
}) => {
    // Handle empty state
    if (!descriptors || descriptors.length === 0) {
        return emptyState;
    }

    return (
        <div
            className={className}
            ref={containerRef}
            role="log"
            aria-live="polite"
            aria-label="Chat messages"
        >
            {descriptors.map((descriptor) => (
                <ChatMessageContainer
                    key={descriptor.id}
                    descriptor={descriptor}
                    onAction={onAction}
                />
            ))}
        </div>
    );
});

ChatMessages.displayName = 'ChatMessages';

ChatMessages.propTypes = {
    /** Array of UI descriptors to render */
    descriptors: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        component: PropTypes.string,
        type: PropTypes.string,
        data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        children: PropTypes.array,
        isStreaming: PropTypes.bool,
        isComplete: PropTypes.bool,
        error: PropTypes.bool
    })),
    /** Callback for component actions */
    onAction: PropTypes.func,
    /** Component to show when descriptors is empty */
    emptyState: PropTypes.node,
    /** Additional CSS class */
    className: PropTypes.string,
    /** Ref for the container div */
    containerRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ])
};

export default ChatMessages;
